import { config } from "@src/config";
import {
    ABExplorationKeyWords,
    FilterOperator,
    SupportedMeasureType,
    SupportedModelAlias,
} from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { useLoadAbExploration } from "@src/hooks/ab-testing/use-ab-exploration-get-result";
import { useGetGeneralField } from "@src/hooks/ab-testing/use-get-general-field";
import { IJobMeta, IMetricEvaluationResult, IQueryOptions } from "@src/types/ab-testing-exploration";
import { flattenNestedRecord } from "@src/util/common/convert-nested-record-to-array";
import { mapRetentionDataToAntdTable } from "@src/util/table/map-to-antd-table";
import { RawDataTable } from "@src/util/table/render-raw-data-table";
import { FormInstance, Skeleton, Table } from "antd";
import React, { memo, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { RetentionCellRender } from "./cell-render";
import { ChartWrapperContextProvider, useChartWrapperContext } from "@src/contexts/ab-testing/chart-wrapper-context";
import { ChartAction } from "../action";
import { toTitle } from "@src/helpers/text-helper";
import { SpotLightEnum } from "@src/pages/ab-test-explore/enum";
import { ReportSpotLight } from "../metric-performance/component/metric-performance-item/report-data-v2/report-spot-light";
import { ChartLayoutWithUserPercent } from "../../layout/chart-layout-with-user-percent";
import { DataCopyClipboard } from "@src/components/data-copy-clipboard";
import { RetentionSignificance } from "./retention-significant";
import { RetentionChart } from "./retention-chart";

const RetentionShowWrapperInternal: React.FC<{
    form: FormInstance<any>;
    applyValue: {
        breakdownBy: string;
        breakdownValue: string[];
        significant: number;
    };
    metricType: "retention" | "sum_retention";
}> = ({ form, applyValue, metricType }) => {
    const [reportSpotLight, setReportSpotLight] = useState(SpotLightEnum.PercentChange);
    const [significanceResult, setSignificanceResult] = useState<
        Record<string, Record<string, Record<string, IMetricEvaluationResult>>>
    >({} as Record<string, Record<string, Record<string, IMetricEvaluationResult>>>);
    const {
        productCode,
        explorationFilters,
        explorationOptions,
        maxDayDiff,
        timeRange,
        variants,
        getExpAliasById,
        baseVariant,
    } = useGetGeneralField({
        form,
    });

    const getQuery = (extraQuery: { dimensions?: string[]; measures?: string[]; filters?: any[] }) => {
        return {
            dimensions: ["exp_group", ...(extraQuery.dimensions ?? [])],
            measures: ["user_count", ...(extraQuery.measures ?? [])],
            limit: 10000,
            order: [["user_count", "desc"]],
            filters: [
                ...explorationFilters,
                {
                    member: ABExplorationKeyWords.ACT_DATE,
                    operator: FilterOperator.BETWEEN,
                    values: timeRange?.map((v) => v.format(config.DATE_FORMAT)) ?? [],
                },
                {
                    member: ABExplorationKeyWords.DAY_DIFF,
                    operator: FilterOperator.BETWEEN,
                    values: [0, maxDayDiff],
                },
            ].concat(extraQuery.filters ?? []),
        };
    };

    const getFullMetrics = () => {
        const result: { mean: string; std?: string }[] = [];
        for (let i = 0; i <= maxDayDiff; i++) {
            result.push({
                mean: `${metricType === "retention" ? "retention" : "sum_retention_per_user"}${i}`,
                std: metricType === "retention" ? undefined : `stddev_samp_sum_retention${i}`,
            });
        }
        return result;
    };
    const fullMetrics = getFullMetrics();
    const detailQuery = getQuery({
        dimensions: applyValue.breakdownBy ? [applyValue.breakdownBy] : [],
        measures: fullMetrics.reduce((acc, item) => {
            if (metricType === "retention") {
                acc.push(item.mean);
            } else {
                acc.push(item.mean);
                if (item.std) {
                    acc.push(item.std);
                }
            }

            return acc;
        }, [] as string[]),
        filters: applyValue.breakdownBy
            ? [
                  {
                      member: applyValue.breakdownBy,
                      operator: FilterOperator.EQUALS,
                      values: applyValue.breakdownValue ?? [],
                  },
              ]
            : [],
    });

    const { result, isLoading, renderedQuery, meta, elapsed } = useLoadAbExploration({
        productCode: productCode,
        modelAlias: SupportedModelAlias.FIREBASE_ENGAGEMENT,
        query: detailQuery,
        options: explorationOptions,
        isSkip: !productCode || !timeRange,
    });

    const { columns, dataSource } = mapRetentionDataToAntdTable({
        rawData: result || [],
        variants: variants ?? [],
        fullMetricDayDiff: fullMetrics.map((item) => item.mean),
        hasBreakdown: applyValue.breakdownBy ? true : false,
        breakdownField: applyValue.breakdownBy,
        breakdownValues: applyValue.breakdownValue ?? [],
        metricType,
    });

    const getColumnWithSignificance = (column: any, breakdownValue: string) => {
        if (variants?.some((v) => v === column.key)) {
            return {
                ...column,
                title: getExpAliasById(column.key as string),
                width: 200,
                render: (value: number, record: any) => {
                    const exp_value = column.key as string;
                    const significanceInfo = significanceResult[breakdownValue]?.[record.metric]?.[exp_value];

                    return (
                        <RetentionCellRender
                            reportSpotLight={reportSpotLight}
                            exp_value={exp_value}
                            getExpAliasById={getExpAliasById}
                            significantInfo={significanceInfo}
                            significanceLevel={applyValue.significant}
                            isBaseVariant={column.key === baseVariant}
                            measureType={
                                metricType === "retention" ? SupportedMeasureType.PROPORTION : SupportedMeasureType.MEAN
                            }
                        />
                    );
                },
            };
        }
        return {
            ...column,
            render:
                column.key === "breakdown"
                    ? (value: number) => {
                          return <span className="table-group-custom font-bold">{value}</span>;
                      }
                    : column.render,
            width: column.key === "metricRetention" ? 120 : column.width,
        };
    };
    const columnsWithSignificance = columns.map((column) => getColumnWithSignificance(column, "-"));

    const significanceResultArray = React.useMemo(() => {
        return flattenNestedRecord(
            significanceResult,
            applyValue.breakdownBy ? [applyValue.breakdownBy, "metric", "variant_group"] : ["metric", "variant_group"],
        );
    }, [significanceResult, applyValue.breakdownBy]);

    return (
        <div>
            <ChartLayoutWithUserPercent
                defaultMode="chart"
                moreActions={(mode) => (
                    <div className="-translate-x-2 translate-y-6">
                        <ChartWrapperContextProvider>
                            <div className="flex justify-end items-center">
                                {mode === "chart" && (
                                    <ReportSpotLight
                                        reportSpotLight={reportSpotLight}
                                        setReportSpotLight={setReportSpotLight}
                                        exclude={[SpotLightEnum.AbsoluteChange]}
                                    />
                                )}

                                <RetentionShowMore
                                    significanceResultArray={significanceResultArray}
                                    form={form}
                                    explorationOptions={explorationOptions}
                                    detailQuery={detailQuery}
                                    renderedQuery={renderedQuery}
                                    meta={meta}
                                    elapsed={elapsed}
                                    metricType={metricType}
                                />
                                {mode === "raw" && <DataCopyClipboard rawData={significanceResultArray} />}
                            </div>
                        </ChartWrapperContextProvider>
                    </div>
                )}
                isHasOtherChart={true}
                renderOtherChart={
                    <RetentionChart
                        metrics={fullMetrics.map((item) => item.mean)}
                        data={significanceResult as any}
                        breakdown={applyValue.breakdownBy ? { values: applyValue.breakdownValue } : undefined}
                        variantsOption={variants?.map((v) => ({ label: getExpAliasById(v), value: v }))}
                        chartHeight={500}
                        style={{
                            padding: "20px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            backgroundColor: "#fafafa",
                            maxHeight: "800px",
                            overflowY: "scroll",
                        }}
                        metricType={metricType}
                    />
                }
                renderRaw={
                    <RawDataTable
                        data={significanceResultArray}
                        fixField={
                            applyValue.breakdownBy
                                ? [applyValue.breakdownBy, "metric", "exp_group"]
                                : ["metric", "exp_group"]
                        }
                        // priorityFields={["exp_group"]}
                        excludeFields={["variant_group", "base_group", "user_count"]}
                        customRenderers={{
                            pValue: (value: number) => value?.toFixed(6) || "-",
                            tStats: (value: number) => value?.toFixed(4) || "-",
                            upliftPercent: (value: number) => (value ? `${(value * 100).toFixed(2)}%` : "-"),
                            lowerBoundUpliftPercent: (value: number) => (value ? `${(value * 100).toFixed(2)}%` : "-"),
                            upperBoundUpliftPercent: (value: number) => (value ? `${(value * 100).toFixed(2)}%` : "-"),
                        }}
                    />
                }
                renderChart={
                    <div
                        css={
                            applyValue.breakdownBy
                                ? css``
                                : css`
                                      .ant-table-cell {
                                          &:has(.custom-cell) {
                                              padding: 0 !important;
                                          }
                                      }
                                  `
                        }
                    >
                        <Table
                            columns={columnsWithSignificance}
                            dataSource={dataSource}
                            loading={isLoading}
                            pagination={false}
                            scroll={{ x: "max-content", y: 800 }}
                            bordered
                            style={{ marginTop: "16px" }}
                            expandable={
                                applyValue.breakdownBy
                                    ? {
                                          expandedRowRender: (record) => {
                                              const nestedColumnsWithSignificance = record.nestedColumns.map(
                                                  (column: any) => getColumnWithSignificance(column, record.breakdown),
                                              );
                                              return (
                                                  <div
                                                      css={css`
                                                          .ant-table-cell {
                                                              &:has(.custom-cell) {
                                                                  padding: 0 !important;
                                                              }
                                                          }
                                                      `}
                                                  >
                                                      <Table
                                                          columns={nestedColumnsWithSignificance}
                                                          dataSource={record.nestedDataSource}
                                                          pagination={false}
                                                          size="small"
                                                          showHeader={true}
                                                          bordered={false}
                                                          style={{ marginLeft: "20px" }}
                                                      />
                                                  </div>
                                              );
                                          },
                                          expandRowByClick: true,
                                          defaultExpandAllRows: true,
                                      }
                                    : undefined
                            }
                        />
                    </div>
                }
                loading={isLoading}
            />

            {!isLoading && (
                <RetentionSignificance
                    form={form}
                    result={result}
                    setSignificanceResult={setSignificanceResult}
                    applyValue={applyValue}
                    fullMetrics={fullMetrics}
                    metricType={metricType}
                />
            )}
        </div>
    );
};
export const RetentionShowWrapper = memo(RetentionShowWrapperInternal, (prevProps, nextProps) => {
    return JSON.stringify([prevProps.applyValue]) === JSON.stringify([nextProps.applyValue]);
});

const RetentionShowMore: React.FC<{
    significanceResultArray: Record<string, any>[];
    form: FormInstance<any>;
    explorationOptions: IQueryOptions;
    detailQuery: Record<string, any>;
    renderedQuery?: string;
    meta?: IJobMeta;
    elapsed?: number;
    metricType: "retention" | "sum_retention";
}> = ({ form, significanceResultArray, explorationOptions, detailQuery, renderedQuery, meta, elapsed, metricType }) => {
    const { updateChartState } = useChartWrapperContext();
    useEffect(() => {
        updateChartState({
            chartKey: `${metricType}-table`,
            dataSource: significanceResultArray,
            modelAlias: SupportedModelAlias.FIREBASE_ENGAGEMENT,
            queryOption: explorationOptions,
            jsonQuery: detailQuery,
            renderedQuery,
            meta,
            elapsed,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify({ significanceResultArray, explorationOptions, detailQuery })]);

    return <ChartAction tabKey={`${metricType}-table`} tabName={toTitle(`${metricType}-table`)} form={form} />;
};
